import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/provider/sidebar/sidebar-context';
import styles from './signing-requests.module.css';
import { DataTable } from '@/components/data-table/data-table';
import { ColumnDef } from '@tanstack/react-table';
import { getUserSigningRequests } from '@/use-cases/get-user-signing-requests';
import { getSignedDocumentDownloadUrl } from '@/use-cases/get-signed-document-download-url';
import { SignRequest } from '@/entities/sign-request';
import { DateTime } from 'luxon';
import { FileText, ArrowUpDown, ArrowDownToLine, PenLine } from 'lucide-react';
import { SignRequestedDocumentModal } from './components/sign-requested-document-modal/sign-requested-document-modal';
import { SignRequestStatusBadge } from './components/sign-request-status-badge/sign-request-status-badge';
import notifyHB from '@/config/notifyHB';
import { hasSeenTour, startTour } from '@/hooks/use-onboarding-tour';

const SIGNING_REQUESTS_TOUR_STEPS = [
	{
		element: '[data-tour="signing-requests-heading"]',
		popover: {
			title: 'Solicitudes de firma',
			description:
				'Aquí aparecen los documentos que otros te han enviado para que firmes.',
		},
	},
	{
		element: '[data-tour="signing-requests-table"]',
		popover: {
			title: 'Tu bandeja de entrada',
			description:
				'Cada fila es una solicitud pendiente. Presta atención a la fecha de expiración — las solicitudes no duran para siempre.',
		},
	},
	{
		element: '[data-tour="signing-requests-action"]',
		popover: {
			title: 'Firmar',
			description:
				'Haz clic en Firmar para revisar el documento y aplicar tu firma digital.',
			side: 'left' as const,
		},
	},
];

export const SigningRequests = () => {
	const { hideSidebar } = useSidebar();
	const [userSignRequests, setUserSignRequests] = useState<SignRequest[]>([]);
	const [selectedSigningRequest, setSelectedSigningRequest] =
		useState<SignRequest | null>(null);
	const [dataLoaded, setDataLoaded] = useState(false);

	const downloadDocument = (signRequest: SignRequest) => {
		const path = `${signRequest.RequestedBy}/${signRequest.DocUlid}/signed/${signRequest.DocName}`;
		getSignedDocumentDownloadUrl(path)
			.then((url) => {
				if (url) window.open(url, '_blank');
			})
			.catch((error) =>
				notifyHB(
					'signing-requests',
					'SigningRequests',
					`Fail to download signed document with ${error}`,
				),
			);
	};

	const sortableHeader =
		(label: string): ColumnDef<SignRequest>['header'] =>
		({ column }) => (
			<Button
				className="-ml-3"
				onClick={() =>
					column.toggleSorting(column.getIsSorted() === 'asc')
				}
				size="sm"
				variant="ghost"
			>
				{label}
				<ArrowUpDown className="ml-1 h-3.5 w-3.5" />
			</Button>
		);

	const myDocumentsTableData: ColumnDef<SignRequest>[] = [
		{
			accessorKey: 'DocName',
			header: sortableHeader('Nombre'),
			cell: ({ row }) => (
				<div className={styles['name-cell']}>
					<FileText className={styles['name-icon']} />
					<span
						className={styles['name-text']}
						title={row.getValue('DocName')}
					>
						{row.getValue('DocName')}
					</span>
				</div>
			),
		},
		{
			accessorKey: 'Status',
			header: () => 'Estado',
			cell: ({ row }) => (
				<SignRequestStatusBadge status={row.getValue('Status')} />
			),
		},
		{
			accessorKey: 'RequesterName',
			header: () => 'Solicitada por',
			cell: ({ row }) => (
				<div className={styles['requester-cell']}>
					{row.original.RequesterName?.toLowerCase().replace(
						',',
						'',
					) ?? null}
				</div>
			),
		},
		{
			accessorKey: 'RequestedAt',
			header: sortableHeader('Solicitada'),
			cell: ({ row }) => (
				<span className={styles['date-cell']}>
					{DateTime.fromISO(row.getValue('RequestedAt')).toFormat(
						'yyyy-MM-dd HH:mm',
					)}
				</span>
			),
		},
		{
			accessorKey: 'Expiration',
			header: sortableHeader('Expiración'),
			cell: ({ row }) => (
				<span className={styles['date-cell']}>
					{row.getValue('Expiration')
						? DateTime.fromISO(
								row.getValue('Expiration'),
							).toFormat('yyyy-MM-dd HH:mm')
						: '-'}
				</span>
			),
		},
		{
			id: 'actions',
			header: '',
			cell: ({ row }) => {
				if (row.original.Status === 'SIGNED') {
					return (
						<div className={styles['actions-cell']}>
							<Button
								variant="ghost"
								size="icon"
								title="Descargar"
								aria-label="Descargar"
								onClick={() => downloadDocument(row.original)}
							>
								<ArrowDownToLine />
							</Button>
						</div>
					);
				}
				if (row.original.Status !== 'PENDING') return null;
				return (
					<div className={styles['actions-cell']}>
						<Button
							data-tour={
								row.index === 0
									? 'signing-requests-action'
									: undefined
							}
							variant="default"
							size="sm"
							onClick={() =>
								setSelectedSigningRequest(row.original)
							}
						>
							<PenLine />
							Firmar
						</Button>
					</div>
				);
			},
		},
	];

	useEffect(() => {
		getUserSigningRequests()
			.then((serverUserDocuments) => {
				if (serverUserDocuments) {
					setUserSignRequests(
						[...serverUserDocuments].sort(
							(a, b) =>
								new Date(b.RequestedAt).getTime() -
								new Date(a.RequestedAt).getTime(),
						),
					);
				}
			})
			.catch((error) =>
				notifyHB(
					'signing-requests',
					'SigningRequests',
					`Fail to get User sign requests with ${error}`,
				),
			)
			.finally(() => setDataLoaded(true));
	}, []);

	useEffect(() => {
		if (!dataLoaded) return;
		if (hasSeenTour('signing-requests')) return;
		const steps = userSignRequests.length > 0
			? SIGNING_REQUESTS_TOUR_STEPS
			: SIGNING_REQUESTS_TOUR_STEPS.slice(0, 2);
		startTour('signing-requests', steps);
	}, [dataLoaded]);

	return (
		<div className={styles['my-documents']}>
			<SignRequestedDocumentModal
				key={
					(selectedSigningRequest?.DocUlid ?? '') +
					(selectedSigningRequest?.RequestedAt.toString() ?? '')
				}
				selectedSigningRequest={selectedSigningRequest}
				onClose={() => setSelectedSigningRequest(null)}
			/>
			<div className={'cyber-backdrop ' + styles.content} />
			<div className="cyber-main with-sidenav" onClick={hideSidebar}>
				<div className="cyber-spacer section"></div>
				<div className={styles.container}>
					<div className={styles.body}>
						<div className={styles.header}>
							<h1 data-tour="signing-requests-heading">Mis solicitudes de firma</h1>
						</div>
						<div data-tour="signing-requests-table">
							<DataTable
								columns={myDocumentsTableData}
								data={userSignRequests}
								isSortable
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
